/**
 * Mobile Filter Modal Component
 * ============================
 * 
 * Advanced filtering and sorting modal component for mobile and desktop interfaces.
 * This component provides a comprehensive filtering interface that supports multiple
 * operation types, dynamic form field selection, and real-time filter application.
 * 
 * Core Features:
 * - Dynamic form field-based filtering with multiple operation types
 * - Advanced sorting with field and direction selection
 * - Form type-specific filtering with dropdown selection
 * - Date and number field comparison operations
 * - Real-time filter application and state management
 * - Local state management for modal interactions
 * 
 * Filter Operations:
 * - Text fields: Equals, Contains
 * - Date/Number fields: Equals, Contains, Greater Than, Less Than
 * - Form Type: Dropdown selection with API format conversion
 * - Dynamic operation availability based on field type
 * 
 * Sort Capabilities:
 * - Field-based sorting with ascending/descending options
 * - Visual indicators for active sort state
 * - Default sort direction management
 * - Sort state synchronization with parent component
 * 
 * Technical Implementation:
 * - Input-driven form field configuration
 * - Local state management for modal interactions
 * - Change detection optimization for UI updates
 * - Event emission for parent component coordination
 * - Dynamic operation generation based on field types
 * 
 * @author MacSafety Development Team
 * @version 1.0
 */

import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { TableColumn } from '../entry-list/dynamic-table/dynamic-table.component';
import { FormMetadata } from '../../../../../models/form-registry';

@Component({
  selector: 'app-mobile-filter-modal',
  templateUrl: './filter-entries.component.html',
  styleUrls: ['./filter-entries.component.css']
})
export class MobileFilterModalComponent implements OnInit, OnChanges {
 
  @Input() columns: TableColumn[] = [];
  @Input() sortState: {column: string, direction: 'asc' | 'desc'} = {column: '', direction: 'asc'};
  @Input() filterState: {column: string, value: string} = {column: '', value: ''};
  @Input() formTypes: FormMetadata[] = [];


  @Output() sort = new EventEmitter<{column: string, direction: 'asc' | 'desc', advancedFilter: boolean}>();
  @Output() filter = new EventEmitter<{column: string, value: string, advancedFilter: boolean, operation: 'equals' | 'contains' | 'greaterThan' | 'lessThan'}>();
  @Output() clearFilter = new EventEmitter<void>();


  // Local filter state for the modal
  localFilterColumn: string = '';
  localFilterValue: string = '';
  localFilterOperation: 'equals' | 'contains' | 'greaterThan' | 'lessThan' = 'equals';
  hasActiveFilter: boolean = false;

  // Local sort state for the modal
  localSortColumn: string = '';
  localSortDirection: 'asc' | 'desc' = 'asc';

  constructor(private cdr: ChangeDetectorRef) {

  }

  /**
   * Lifecycle hook that responds to input property changes.
   * Synchronizes local state with parent component state and manages
   * UI updates when columns, sort state, or filter state change.
   * 
   * @param changes - Object containing information about changed properties
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes['columns']) {
      this.columns = changes['columns'].currentValue;
      this.resetLocalFilterState();
      this.resetLocalSortState();
      this.cdr.detectChanges();
    }
    if (changes['sortState']) {
      this.sortState = changes['sortState'].currentValue;
      this.localSortColumn = this.sortState.column;
      this.localSortDirection = this.sortState.direction;
     
    }
    if (changes['filterState']) {
      this.filterState = changes['filterState'].currentValue;
      this.localFilterColumn = this.filterState.column;
      this.localFilterValue = this.filterState.value;
      if (this.localFilterColumn!=='' && this.localFilterValue!=='') {
        this.hasActiveFilter = true;
      }
    }

  }

  /**
   * Lifecycle hook that is called after data-bound properties are initialized.
   * Currently minimal implementation as initialization is handled in ngOnChanges.
   */
  ngOnInit() {

  }



  /**
   * Resets the local filter state to initial values.
   * Clears filter column, value, operation, and active filter flag.
   * 
   * @private
   */
  private resetLocalFilterState() {
    this.localFilterColumn = '';
    this.localFilterValue = '';
    this.localFilterOperation = 'equals';
    this.hasActiveFilter = false;
  }

  /**
   * Resets the local sort state to initial values.
   * Clears sort column and sets direction to ascending.
   * 
   * @private
   */
  private resetLocalSortState() {
    this.localSortColumn = '';
    this.localSortDirection = 'asc';
  }

  /**
   * Handles filter column selection changes.
   * Clears the filter value and resets active filter state when
   * a new column is selected, ensuring clean filter application.
   */
  onFilterColumnChange() {
    // Clear the filter value when a new column is selected
    this.localFilterValue = '';
    this.hasActiveFilter = false;
    
    // Force change detection to ensure UI updates
    this.cdr.detectChanges();
  }

  /**
   * Applies the current filter configuration.
   * Emits the filter event with column, value, and operation when
   * both column and value are properly set.
   */
  onFilter() {
    if (this.localFilterColumn && this.localFilterValue.trim()) {
      this.filter.emit({ column: this.localFilterColumn, value: this.localFilterValue.trim(), advancedFilter: true, operation: this.localFilterOperation });
      this.hasActiveFilter = true;
    }
  }

  /**
   * Clears the current filter and resets local state.
   * Emits clear filter event to parent component and resets
   * local filter state to initial values.
   */
  onClearFilter() {
    this.resetLocalFilterState();
    this.clearFilter.emit();
    
    // Force change detection to ensure UI updates
    this.cdr.detectChanges();
  }

  /**
   * Handles sort column selection changes.
   * Sets default sort direction to ascending and immediately emits
   * the sort event with the new column selection.
   */
  onSortColumnChange() {
    // Set default sort direction when a new column is selected
    this.localSortDirection = 'asc';
    
    // Force change detection to ensure the sort direction dropdown appears
    this.cdr.detectChanges();
    
    // Emit the sort event
    this.sort.emit({ column: this.localSortColumn, direction: 'asc', advancedFilter: true });
  }

  /**
   * Handles sort direction selection changes.
   * Emits the sort event with the current column and new direction
   * when a direction is selected.
   */
  onSortDirectionChange() {
    // Only emit if a direction is selected
    if (this.localSortDirection) {
      this.sort.emit({ column: this.localSortColumn, direction: this.localSortDirection, advancedFilter: true });
    }
  }

  /**
   * Clears the current sort configuration.
   * Resets local sort state and emits clear sort event to parent component.
   */
  onClearSort() {
    this.localSortColumn = '';
    this.localSortDirection = 'asc';
    this.sort.emit({ column: '', direction: 'asc', advancedFilter: true });
    
    // Force change detection to ensure UI updates
    this.cdr.detectChanges();
  }

  /**
   * Clears all filters and sorting configurations.
   * Calls both clear filter and clear sort methods to reset
   * the entire modal state.
   */
  onClearAll(): void {
    this.onClearFilter();
    this.onClearSort();
  }



  /**
   * Gets the appropriate input type for filter inputs based on column type.
   * Returns 'date' for date columns and 'text' for all other types.
   * 
   * @param columnType - The column type to get input type for
   * @returns String input type for the filter input
   */
  getFilterInputType(columnType: string): string {
    switch (columnType) {
      case 'date':
        return 'date';
      default:
        return 'text';
    }
  }

  /**
   * Gets the placeholder text for filter inputs based on column type.
   * Returns format-specific placeholders for different column types.
   * 
   * @param columnType - The column type to get placeholder for
   * @returns String placeholder text for the filter input
   */
  getFilterPlaceholder(columnType: string): string {
    switch (columnType) {
      case 'date':
        return 'Search in YYYY-MM-DD';
      default:
        return 'Enter filter value';
    }
  }

  /**
   * Gets the column type for the currently selected filter column.
   * Returns the type from the columns configuration or defaults to 'text'.
   * 
   * @returns String column type for the selected filter column
   */
  getSelectedColumnType(): string {
    if (!this.localFilterColumn) return 'text';
    const selectedColumn = this.columns.find(col => col.key === this.localFilterColumn);
    return selectedColumn?.type || 'text';
  }

  /**
   * Gets the available filter operations based on the selected column type.
   * Returns different operation sets for text vs. date/number columns.
   * 
   * @returns Array of available operations with value and label
   */
  getAvailableOperations(): { value: 'equals' | 'contains' | 'greaterThan' | 'lessThan', label: string }[] {
    const columnType = this.getSelectedColumnType();
    const operations: { value: 'equals' | 'contains' | 'greaterThan' | 'lessThan', label: string }[] = [
      { value: 'equals', label: 'Equals' },
      { value: 'contains', label: 'Contains' }
    ];

    // Add date/number specific operations
    if (columnType === 'date' || columnType === 'number') {
      operations.push(
        { value: 'greaterThan', label: 'Greater Than or Equal To' },
        { value: 'lessThan', label: 'Less Than or Equal To' }
      );
    }

    return operations;
  }

  /**
   * Handles form type filter application from dropdown selection.
   * Converts hyphenated form types to underscore format for API compatibility
   * and emits the filter event with the converted value.
   * 
   * @param event - The select change event containing the selected form type
   * @param column - The column key (should be 'formType')
   */
  onFilterApplyFormType(event: any, column: string) {
    const selectedFormType = event.detail.value;
    
    if (selectedFormType) {
      // Convert hyphenated form type to underscore format for API filtering
      const underscoreFormType = selectedFormType.replace(/-/g, '_');
      
      // Find the FormMetadata object that matches the selected formType
      const formMetadata = this.formTypes.find(type => type.formType === selectedFormType);
      
      if (formMetadata) {
        this.filter.emit({ column, value: underscoreFormType, advancedFilter: true, operation: this.localFilterOperation });
        this.hasActiveFilter = true;
      }
    }
  }
}